import API_CONFIG, { getHeaders, getApiUrl } from './config';
import { getRastId } from '../../utils/rastIdManager';

/**
 * Shape of the request-otp / verify-otp response payload.
 * @typedef {Object} AuthResponseData
 * @property {boolean} [success]
 * @property {string} [message]
 * @property {boolean} otpRequired - When false, auth is complete and authToken is present (OTP feature flag OFF). When true, the OTP entry/verify flow is required.
 * @property {string|null} [authToken] - JWT issued when authentication completes without OTP.
 * @property {Object|null} [customerDto] - Customer details returned alongside the token.
 * @property {string} [expiresAt] - OTP expiry timestamp (only when otpRequired is true).
 * @property {string} [deliveryMethod] - Channel the OTP was sent through (only when otpRequired is true).
 */

class AuthService {
  /**
   * Persist the auth token and customer details so that subsequent requests
   * are authenticated. Mirrors how the verify-otp response token was stored.
   * @param {AuthResponseData} data
   * @param {string} phoneNumber
   * @param {string} [email]
   */
  persistAuthSession(data, phoneNumber, email) {
    if (data.authToken) {
      localStorage.setItem('authToken', data.authToken);
    }

    localStorage.setItem('phoneNumber', phoneNumber);
    if (email) {
      localStorage.setItem('email', email);
    }

    if (data.customerDto) {
      if (data.customerDto.name) {
        localStorage.setItem('customerName', data.customerDto.name);
      }
      if (data.customerDto.id) {
        localStorage.setItem('customerId', data.customerDto.id);
      }
    }
  }

  parseApiPayload(response, responseData, fallbackMessage) {
    if (!response.ok || (responseData.statusCode && responseData.statusCode >= 400)) {
      throw new Error(responseData.message || fallbackMessage);
    }

    if (responseData.data == null) {
      throw new Error(responseData.message || fallbackMessage);
    }

    return responseData.data;
  }

  async requestOtp(phoneNumber, rastId, email, deliveryMethod) {
    try {
      const finalRastId = rastId || getRastId();
      
      if (!finalRastId) {
        throw new Error('Restaurant ID not found. Please scan QR code again.');
      }
      
      const url = `${getApiUrl(API_CONFIG.ENDPOINTS.AUTH.REQUEST_OTP)}?rastId=${encodeURIComponent(finalRastId)}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(false, {
          'X-Customer-Email': email || ''
        }),
        body: JSON.stringify({
          phoneNumber,
          email,
          deliveryMethod
        })
      });

      const responseData = await response.json();

      /** @type {AuthResponseData} */
      const data = this.parseApiPayload(response, responseData, 'Failed to send OTP');

      // OTP feature flag OFF: backend completes auth in one step and returns a
      // token. Persist it here so the caller can go straight to logged-in state.
      if (data.otpRequired === false) {
        this.persistAuthSession(data, phoneNumber, email);
      }

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Request OTP error:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  async verifyOtp(phoneNumber, email, otpCode) {
    try {
      const url = getApiUrl(API_CONFIG.ENDPOINTS.AUTH.VERIFY_OTP);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(false, {
          'X-Customer-Email': email || ''
        }),
        body: JSON.stringify({
          phoneNumber,
          otpCode
        })
      });

      const responseData = await response.json();

      const data = this.parseApiPayload(response, responseData, 'Invalid OTP');

      this.persistAuthSession(data, phoneNumber, email);

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Verify OTP error:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  async createCustomerTicket(name, phoneNumber, email, rastId) {
    try {
      const url = getApiUrl(API_CONFIG.ENDPOINTS.CUSTOMER.CREATE_TICKET);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(true, {
          'X-Customer-Email': email || ''
        }),
        body: JSON.stringify({
          phoneNumber,
          name,
          email,
          rastId
        })
      });

      const responseData = await response.json();

      const data = this.parseApiPayload(response, responseData, 'Failed to create customer ticket');

      const resolvedCustomerName = data.customerDto?.name || name;
      if (resolvedCustomerName) {
        localStorage.setItem('customerName', resolvedCustomerName);
      }
      if (data.customerDto?.id) {
        localStorage.setItem('customerId', data.customerDto.id);
      }
      localStorage.setItem('customerTicketCreated', 'true');
      
      // Handle both response structures: data.id or data.customerTicketDto.id
      const customerTicketId = data.customerTicketDto?.id || data.id;
      if (customerTicketId) {
        localStorage.setItem('customerTicketId', customerTicketId);
        console.log('Customer ticket cached:', customerTicketId);
      } else {
        console.warn('Customer ticket ID not found in response:', data);
      }

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Create customer ticket error:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  getTokenPayload() {
    const token = this.getAuthToken();
    if (!token) {
      return null;
    }

    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch (error) {
      console.warn('Failed to parse auth token payload:', error);
      return null;
    }
  }

  isAuthenticated() {
    return !!this.getAuthToken();
  }

  hasCustomerTicket() {
    return localStorage.getItem('customerTicketCreated') === 'true';
  }

  getCustomerData() {
    const tokenPayload = this.getTokenPayload();
    const tokenName = tokenPayload?.customerName;
    const tokenCustomerId = tokenPayload?.customerId;

    return {
      phoneNumber: localStorage.getItem('phoneNumber'),
      email: localStorage.getItem('email'),
      name: localStorage.getItem('customerName') || (tokenName && tokenName.trim() ? tokenName : null),
      rastId: getRastId(),
      authToken: this.getAuthToken(),
      customerTicketId: localStorage.getItem('customerTicketId'),
      customerId: localStorage.getItem('customerId') || tokenCustomerId
    };
  }

  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('phoneNumber');
    localStorage.removeItem('email');
    localStorage.removeItem('customerName');
    localStorage.removeItem('customerTicketCreated');
    localStorage.removeItem('customerTicketId');
    localStorage.removeItem('customerId');
  }
}

export default new AuthService();
